<template>
  <div class="om-page">
    <div class="om-page-header">
      <h1 class="om-page-title">Смета, заказ и списание М-19</h1>
      <p class="om-page-sub">Количество: утверждённая смета команды проекта, накладные и М-19, отправленные в 1С. Без сумм. «Все записи» — лимиты, материалы вне сметы и все типы закупа.</p>
    </div>

    <div class="om-filters-card">
      <div class="om-filters-card__body">
        <div class="om-filters-toolbar">
          <div class="om-filters-main">
            <CustomSelect
              class="om-filter-field"
              :options="residents"
              label="ЖК"
              option-value-key="resident_id"
              option-label-key="resident_name"
              :value="filters.resident_id"
              clearable
              fullWidth
              placeholder="Выберите.."
              :useInput="!filters.resident_id"
              @change="onResident"
            />
            <CustomSelect
              class="om-filter-field"
              :options="projectTypes"
              label="Тип проекта"
              option-value-key="project_type_id"
              option-label-key="project_type_name"
              clearable
              fullWidth
              placeholder="Выберите.."
              :useInput="!filters.project_type_id"
              :value="filters.project_type_id"
              :disabled="!filters.resident_id"
              @change="onProjectType"
            />
            <CustomSelect
              class="om-filter-field"
              :options="entrances"
              label="Подъезд"
              option-value-key="entrance"
              option-label-key="entrance_name"
              clearable
              fullWidth
              placeholder="Выберите.."
              :useInput="!filters.entrance"
              :value="filters.entrance"
              :disabled="!filters.resident_id"
              @change="onEntrance"
            />
            <CustomSelect
              class="om-filter-field"
              :options="floors"
              label="Этаж"
              option-value-key="level_num"
              option-label-key="floor_name"
              clearable
              fullWidth
              placeholder="Выберите.."
              :useInput="!filters.level_num"
              :value="filters.level_num"
              :disabled="!filters.resident_id"
              @change="onFloor"
            />
            <CustomSelect
              class="om-filter-field"
              :options="smetaConfirmOptions"
              label="Смета утверждена"
              option-value-key="value"
              option-label-key="label"
              clearable
              fullWidth
              placeholder="Все"
              :value="filters.smeta_confirm"
              :disabled="!filters.resident_id"
              @change="onSmetaConfirm"
            />
          </div>

          <div class="om-filters-actions">
            <div class="om-toggle-wrap">
              <q-toggle
                v-model="filters.show_all"
                label="Все записи"
                dense
                color="primary"
                :disable="!filters.resident_id"
                @update:model-value="onShowAll"
              />
              <div class="om-toggle-hint">Лимиты, вне сметы, все типы закупа</div>
            </div>
            <q-btn
              class="om-filter-action-btn"
              outline
              color="grey-7"
              no-caps
              dense
              :icon="isFullyExpanded ? 'unfold_less' : 'unfold_more'"
              :label="isFullyExpanded ? 'Свернуть' : 'Развернуть'"
              :disable="!treeRoots.length"
              @click="toggleExpandAll"
            />
            <q-btn
              class="om-filter-action-btn"
              outline
              color="grey-7"
              no-caps
              dense
              icon="filter_alt_off"
              label="Сбросить"
              :disable="!hasActiveFilters"
              @click="resetFilters"
            />
            <q-btn
              class="om-filter-action-btn"
              color="positive"
              unelevated
              no-caps
              dense
              icon="download"
              label="Excel"
              :disable="!filters.resident_id || exporting"
              :loading="exporting"
              @click="exportExcel"
            />
          </div>
        </div>
      </div>
    </div>

    <div v-if="materialRows.length" class="om-totals-bar q-mt-sm">
      <div class="om-total-block">
        <div class="om-total-label">Всего</div>
        <div class="om-total-value"><Amount :value="totals.total_qty" round="2" /></div>
      </div>
      <div class="om-totals-sep" />
      <div class="om-total-block">
        <div class="om-total-label">Заказано</div>
        <div class="om-total-value"><Amount :value="totals.ordered_qty" round="2" /></div>
      </div>
      <div class="om-totals-sep" />
      <div class="om-total-block">
        <div class="om-total-label">Списано М-19</div>
        <div class="om-total-value"><Amount :value="totals.m19_qty" round="2" /></div>
      </div>
      <div class="om-totals-sep" />
      <div class="om-total-block">
        <div class="om-total-label">Остаток от заказа</div>
        <div class="om-total-value" :class="{ 'om-neg': totals.remainder_ordered < 0 }">
          <Amount :value="totals.remainder_ordered" round="2" />
        </div>
      </div>
      <div class="om-totals-sep" />
      <div class="om-total-block">
        <div class="om-total-label">Остаток от сметы</div>
        <div class="om-total-value" :class="{ 'om-neg': totals.remainder_smeta < 0 }">
          <Amount :value="totals.remainder_smeta" round="2" />
        </div>
      </div>
    </div>

    <q-banner v-if="errorText" rounded class="om-banner q-mt-md">
      <div class="om-banner__text">{{ errorText }}</div>
    </q-banner>

    <q-linear-progress
      v-if="loading"
      indeterminate
      color="primary"
      track-color="grey-3"
      class="om-progress q-mt-md"
      rounded
      size="4px"
    />

    <q-card v-if="displayRows.length" flat bordered class="om-card om-card--table q-mt-md">
      <div class="om-table-scroll">
        <table class="om-table" :class="{ 'om-table--wide': filters.show_all }" cellspacing="0">
          <colgroup>
            <col class="om-col-tree" />
            <col class="om-col-pid" />
            <col v-if="filters.show_all" class="om-col-src" />
            <col class="om-col-unit" />
            <col class="om-col-num" />
            <col class="om-col-num" />
            <col class="om-col-num" />
            <col class="om-col-num" />
            <col class="om-col-num" />
          </colgroup>
          <thead>
            <tr>
              <th class="om-th om-th--tree om-th--sticky">Уровень</th>
              <th class="om-th om-th--pid">project_id</th>
              <th v-if="filters.show_all" class="om-th">Источник всего</th>
              <th class="om-th">Ед.</th>
              <th class="om-th om-th--num">Всего</th>
              <th class="om-th om-th--num">Заказано</th>
              <th class="om-th om-th--num">Списано М-19</th>
              <th class="om-th om-th--num">Остаток от заказа</th>
              <th class="om-th om-th--num">Остаток от сметы</th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="row in displayRows"
              :key="row._path"
              :class="[
                'om-tr',
                row._isLeaf ? 'om-tr--leaf' : 'om-tr--node',
                `om-tr--d${row._depth}`,
              ]"
            >
              <td
                class="om-td om-td--tree om-td--sticky"
                :style="{ paddingLeft: `${12 + row._depth * 16}px` }"
              >
                <span class="om-tree-cell">
                  <button
                    v-if="!row._isLeaf"
                    type="button"
                    class="om-expand"
                    :aria-expanded="expanded[row._path] === true"
                    @click="toggle(row._path)"
                  >
                    <span
                      class="om-chevron"
                      :class="{ 'om-chevron--open': expanded[row._path] === true }"
                    />
                  </button>
                  <span v-else class="om-expand om-expand--spacer" />
                  <span class="om-tree-label">{{ row._label }}</span>
                </span>
              </td>
              <td class="om-td om-td--pid">{{ row.project_id || '—' }}</td>
              <td v-if="filters.show_all" class="om-td">{{ totalSourceLabel(row) }}</td>
              <td class="om-td">{{ row._depth >= 3 ? (row.unit_name || '—') : '—' }}</td>
              <td class="om-td om-td--num"><Amount :value="row.total_qty" round="2" /></td>
              <td class="om-td om-td--num">
                <Amount v-if="row._showOrdered" :value="row.ordered_qty" round="2" />
                <span v-else class="om-dash">—</span>
              </td>
              <td class="om-td om-td--num"><Amount :value="row.m19_qty" round="2" /></td>
              <td class="om-td om-td--num" :class="{ 'om-neg': isNeg(row.remainder_ordered) && row._showOrdered }">
                <Amount v-if="row._showOrdered" :value="row.remainder_ordered" round="2" />
                <span v-else class="om-dash">—</span>
              </td>
              <td class="om-td om-td--num" :class="{ 'om-neg': isNeg(row.remainder_smeta) }">
                <Amount :value="row.remainder_smeta" round="2" />
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </q-card>

    <q-card
      v-else-if="!loading && searched"
      flat
      bordered
      class="om-empty q-mt-md"
    >
      <q-card-section class="column items-center q-py-xl q-px-md text-center">
        <div class="om-empty__title">Нет данных</div>
        <div class="om-empty__text">Смените фильтры или проверьте данные.</div>
      </q-card-section>
    </q-card>
  </div>
</template>

<script>
import Amount from '@/components/common/Amount.vue';
import CustomSelect from '@/components/form/CustomSelect';
import {
  exportOrderedVsM19Report,
  getOrderedVsM19Report,
  getResourceStatementProjectTypes,
  getResourceStatementResidents,
  getResourceStatementEntrances,
  getResourceStatementFloorsByResident,
} from '@/views/ResidentialSettings/services';

function num(v) {
  if (v == null || v === '') return 0;
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
}

function zeroQty() {
  return {
    total_qty: 0,
    ordered_qty: 0,
    m19_qty: 0,
    remainder_ordered: 0,
    remainder_smeta: 0,
    limit_qty: 0,
    smeta_qty: 0,
  };
}

function floorLabel(row) {
  const block = row.block_name ? `Блок ${row.block_name}` : '';
  const floor = row.level_num != null && row.level_num !== '' ? `этаж ${row.level_num}` : 'этаж —';
  return block ? `${block}, ${floor}` : floor;
}

export default {
  name: 'OrderedVsM19Report',
  components: { CustomSelect, Amount },
  data() {
    return {
      residents: [],
      projectTypes: [],
      entrances: [],
      floors: [],
      smetaConfirmOptions: [
        { value: '1', label: 'Утверждена' },
        { value: '0', label: 'Не утверждена' },
      ],
      filters: {
        resident_id: '',
        project_type_id: '',
        entrance: '',
        level_num: '',
        smeta_confirm: '',
        show_all: false,
      },
      rawRows: [],
      treeRoots: [],
      expanded: {},
      loading: false,
      exporting: false,
      searched: false,
      errorText: '',
    };
  },
  computed: {
    materialRows() {
      return (this.rawRows || []).filter((r) => r.row_grain === 'project_material');
    },
    totals() {
      return this.materialRows.reduce(
        (acc, r) => {
          acc.total_qty += num(r.total_qty);
          acc.ordered_qty += num(r.ordered_qty);
          acc.m19_qty += num(r.m19_qty);
          acc.remainder_ordered += num(r.remainder_ordered);
          acc.remainder_smeta += num(r.remainder_smeta);
          return acc;
        },
        { total_qty: 0, ordered_qty: 0, m19_qty: 0, remainder_ordered: 0, remainder_smeta: 0 },
      );
    },
    hasActiveFilters() {
      const f = this.filters;
      return !!(
        f.resident_id
        || f.project_type_id
        || f.entrance
        || f.level_num
        || f.smeta_confirm
        || f.show_all
      );
    },
    displayRows() {
      const rows = [];
      const walk = (nodes, parentOpen) => {
        if (!parentOpen) return;
        for (const n of nodes) {
          const open = n._isLeaf || this.expanded[n._path] === true;
          rows.push(n);
          if (!n._isLeaf && n.children?.length) walk(n.children, open);
        }
      };
      walk(this.treeRoots, true);
      return rows;
    },
    isFullyExpanded() {
      const check = (nodes) => {
        for (const n of nodes) {
          if (!n._isLeaf && this.expanded[n._path] !== true) return false;
          if (n.children?.length && !check(n.children)) return false;
        }
        return true;
      };
      return this.treeRoots.length > 0 && check(this.treeRoots);
    },
  },
  async mounted() {
    this.residents = (await getResourceStatementResidents()) || [];
  },
  methods: {
    isNeg(v) {
      return num(v) < 0;
    },
    totalSourceLabel(row) {
      if (row._depth !== 3) return '—';
      if (row.total_source === 'smeta') return 'Смета';
      if (row.total_source === 'limit') return 'Лимит';
      return '—';
    },
    toggle(path) {
      this.expanded = { ...this.expanded, [path]: !this.expanded[path] };
    },
    collapseToOverview(roots = this.treeRoots) {
      const next = {};
      for (const n of roots || []) {
        if (!n._isLeaf) next[n._path] = true;
      }
      return next;
    },
    expandAll(roots = this.treeRoots) {
      const next = {};
      const walk = (nodes) => {
        for (const n of nodes) {
          if (!n._isLeaf) {
            next[n._path] = true;
            walk(n.children || []);
          }
        }
      };
      walk(roots || []);
      return next;
    },
    toggleExpandAll() {
      this.expanded = this.isFullyExpanded
        ? this.collapseToOverview()
        : this.expandAll();
    },
    defaultExpanded(roots) {
      return this.collapseToOverview(roots);
    },
    buildTree(flat) {
      const rootChildren = [];
      const findChild = (arr, key, factory) => {
        let n = arr.find((c) => c._path === key);
        if (!n) {
          n = factory();
          arr.push(n);
        }
        return n;
      };

      const projectRows = (flat || []).filter((r) => r.row_grain === 'project_material');
      const floorRows = (flat || []).filter((r) => r.row_grain === 'floor_material');

      const ensureBranch = (r) => {
        const usp = `usp:${r.sbs_project_id ?? 0}`;
        const pt = `${usp}|pt:${r.project_type_id ?? 0}`;
        const pr = `${pt}|p:${r.project_id}`;
        const mat = `${pr}|m:${r.material_id}`;

        const n0 = findChild(rootChildren, usp, () => ({
          _path: usp,
          _depth: 0,
          _isLeaf: false,
          _label: r.sbs_project_name || 'Проект УСП',
          _showOrdered: true,
          project_id: null,
          unit_name: '',
          total_source: null,
          children: [],
          ...zeroQty(),
        }));
        const n1 = findChild(n0.children, pt, () => ({
          _path: pt,
          _depth: 1,
          _isLeaf: false,
          _label: r.project_type_name || 'Конструктив',
          _showOrdered: true,
          project_id: null,
          unit_name: '',
          total_source: null,
          children: [],
          ...zeroQty(),
        }));
        const n2 = findChild(n1.children, pr, () => ({
          _path: pr,
          _depth: 2,
          _isLeaf: false,
          _label: `Проект ${r.project_id}`,
          _showOrdered: true,
          project_id: r.project_id,
          unit_name: '',
          total_source: null,
          children: [],
          ...zeroQty(),
        }));
        const n3 = findChild(n2.children, mat, () => ({
          _path: mat,
          _depth: 3,
          _isLeaf: true,
          _label: r.material_name || 'Материал',
          _showOrdered: true,
          project_id: r.project_id,
          unit_name: r.unit_name || '',
          total_source: null,
          children: [],
          ...zeroQty(),
        }));
        return { n0, n1, n2, n3 };
      };

      for (const r of projectRows) {
        const { n3 } = ensureBranch(r);
        n3._isLeaf = true;
        n3.unit_name = r.unit_name || n3.unit_name;
        n3.total_source = r.total_source;
        n3.total_qty = num(r.total_qty);
        n3.ordered_qty = num(r.ordered_qty);
        n3.m19_qty = num(r.m19_qty);
        n3.remainder_ordered = num(r.remainder_ordered);
        n3.remainder_smeta = num(r.remainder_smeta);
        n3.smeta_qty = num(r.smeta_qty);
        n3.limit_qty = num(r.limit_qty);
      }

      for (const r of floorRows) {
        const { n3 } = ensureBranch(r);
        n3._isLeaf = false;
        const fl = `${n3._path}|f:${r.floor_map_id}`;
        findChild(n3.children, fl, () => ({
          _path: fl,
          _depth: 4,
          _isLeaf: true,
          _label: floorLabel(r),
          _showOrdered: false,
          project_id: r.project_id,
          unit_name: r.unit_name || n3.unit_name,
          total_source: r.total_source,
          children: [],
          total_qty: num(r.total_qty),
          ordered_qty: 0,
          m19_qty: num(r.m19_qty),
          remainder_ordered: 0,
          remainder_smeta: num(r.remainder_smeta),
          smeta_qty: num(r.smeta_qty),
          limit_qty: 0,
        }));
      }

      const sumUp = (node) => {
        if (!node.children?.length) return;
        for (const ch of node.children) sumUp(ch);
        if (node._depth === 3) return;
        node.total_qty = node.children.reduce((a, ch) => a + num(ch.total_qty), 0);
        node.ordered_qty = node.children.reduce((a, ch) => a + num(ch.ordered_qty), 0);
        node.m19_qty = node.children.reduce((a, ch) => a + num(ch.m19_qty), 0);
        node.remainder_ordered = node.children.reduce((a, ch) => a + num(ch.remainder_ordered), 0);
        node.remainder_smeta = node.children.reduce((a, ch) => a + num(ch.remainder_smeta), 0);
      };
      for (const n of rootChildren) sumUp(n);

      const sortNodes = (nodes, depth) => {
        nodes.sort((a, b) => {
          if (depth === 2) return num(a.project_id) - num(b.project_id);
          if (depth === 4) return String(a._label).localeCompare(String(b._label), 'ru');
          return String(a._label).localeCompare(String(b._label), 'ru');
        });
        for (const n of nodes) {
          if (n.children?.length) sortNodes(n.children, n._depth + 1);
        }
      };
      sortNodes(rootChildren, 0);

      this.treeRoots = rootChildren;
      this.expanded = this.defaultExpanded(rootChildren);
    },
    resetFilters() {
      this.filters.resident_id = '';
      this.filters.project_type_id = '';
      this.filters.entrance = '';
      this.filters.level_num = '';
      this.filters.smeta_confirm = '';
      this.filters.show_all = false;
      this.projectTypes = [];
      this.entrances = [];
      this.floors = [];
      this.clearReport();
    },
    clearReport() {
      this.rawRows = [];
      this.treeRoots = [];
      this.expanded = {};
      this.searched = false;
      this.errorText = '';
    },
    async exportExcel() {
      if (!this.filters.resident_id) return;
      this.exporting = true;
      try {
        const params = this.reportParams();
        const resident = this.residents.find(
          (r) => String(r.resident_id) === String(this.filters.resident_id),
        );
        const residentName = resident?.resident_name
          ? resident.resident_name.replace(/[\\/:*?"<>|]/g, '_')
          : 'resident';
        const blob = await exportOrderedVsM19Report(params);
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `smeta_zakaz_m19_${residentName}.xlsx`;
        a.click();
        URL.revokeObjectURL(url);
      } catch (e) {
        this.errorText = e?.response?.data?.message || e?.message || 'Ошибка экспорта';
      } finally {
        this.exporting = false;
      }
    },
    reportParams() {
      const params = {
        resident_id: this.filters.resident_id,
        show_all: this.filters.show_all ? 1 : 0,
      };
      if (this.filters.project_type_id) params.project_type_id = this.filters.project_type_id;
      if (this.filters.entrance) params.entrance = this.filters.entrance;
      if (this.filters.level_num) params.level_num = this.filters.level_num;
      if (this.filters.smeta_confirm === '1' || this.filters.smeta_confirm === '0') {
        params.smeta_confirm = this.filters.smeta_confirm;
      }
      return params;
    },
    async onResident(v) {
      this.filters.resident_id = v;
      this.filters.project_type_id = '';
      this.filters.entrance = '';
      this.filters.level_num = '';
      this.projectTypes = [];
      this.entrances = [];
      this.floors = [];
      if (!v) {
        this.clearReport();
        return;
      }
      const [types, entrances, floors] = await Promise.all([
        getResourceStatementProjectTypes(v),
        getResourceStatementEntrances(v),
        getResourceStatementFloorsByResident(v),
      ]);
      this.projectTypes = types || [];
      this.entrances = entrances || [];
      this.floors = floors || [];
      await this.fetchReport();
    },
    async onProjectType(v) {
      this.filters.project_type_id = v || '';
      if (this.filters.resident_id) await this.fetchReport();
    },
    async onEntrance(v) {
      this.filters.entrance = v || '';
      if (this.filters.resident_id) await this.fetchReport();
    },
    async onFloor(v) {
      this.filters.level_num = v || '';
      if (this.filters.resident_id) await this.fetchReport();
    },
    async onSmetaConfirm(v) {
      this.filters.smeta_confirm = v === 0 || v === '0' ? '0' : (v || '');
      if (this.filters.resident_id) await this.fetchReport();
    },
    async onShowAll() {
      if (this.filters.resident_id) await this.fetchReport();
    },
    async fetchReport() {
      if (!this.filters.resident_id) {
        this.clearReport();
        return;
      }
      this.errorText = '';
      this.loading = true;
      this.searched = true;
      try {
        const res = await getOrderedVsM19Report(this.reportParams());
        this.rawRows = res?.data || [];
        this.buildTree(this.rawRows);
      } catch (e) {
        this.errorText = e?.response?.data?.message || e?.message || 'Ошибка загрузки';
        this.rawRows = [];
        this.treeRoots = [];
        this.expanded = {};
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>

<style scoped lang="scss">
@use "@/assets/styles/quasar.variables.scss" as *;

.om-page {
  padding: 8px 12px 48px;
  max-width: 100%;
  min-height: calc(100vh - 56px);
  min-height: calc(100dvh - 56px);
  box-sizing: border-box;
}

.om-page-header {
  margin-bottom: 8px;
}

.om-page-title {
  margin: 0;
  font-size: 20px;
  font-weight: 600;
  line-height: 1.3;
  color: #0f172a;
}

.om-page-sub {
  margin: 4px 0 0;
  font-size: 13px;
  line-height: 1.4;
  color: #64748b;
}

.om-filters-card {
  margin-bottom: 8px;
  padding: 8px 12px;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  background: linear-gradient(180deg, #fafbfc 0%, #fff 100%);
  box-shadow: 0 1px 2px rgba(15, 23, 42, 0.04);
}

.om-filters-toolbar {
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  align-items: end;
  gap: 12px;
}

.om-filters-main {
  display: grid;
  grid-template-columns: repeat(5, minmax(0, 1fr));
  gap: 12px;
  min-width: 0;
}

.om-filters-actions {
  display: flex;
  flex-wrap: wrap;
  align-items: flex-end;
  justify-content: flex-end;
  gap: 8px;
}

.om-toggle-wrap {
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  min-height: 40px;
  max-width: 220px;
  margin-right: 4px;
}

.om-toggle-hint {
  font-size: 11px;
  line-height: 1.3;
  color: #64748b;
}

.om-filter-field {
  min-width: 0;
  width: 100%;

  :deep(.custom-select__wrapper),
  :deep(.q-field) {
    width: 100%;
  }

  :deep(.q-field__control) {
    min-height: 40px;
  }
}

.om-filter-action-btn {
  font-size: 12px;
  min-height: 36px;
  width: 132px;
  min-width: 132px;
  padding: 0 8px;
}

.om-totals-bar {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  background: #fff;
  border: 1px solid $gray-light;
  border-radius: 8px;
  padding: 10px 16px;
}

.om-totals-sep {
  width: 1px;
  height: 32px;
  background: $gray-light;
  margin: 0 16px;
  flex-shrink: 0;
}

.om-total-label {
  font-size: 11px;
  color: #64748b;
}

.om-total-value {
  font-size: 16px;
  font-weight: 600;
  color: #0f172a;
}

.om-banner {
  background: #fef2f2;
  border: 1px solid #fecaca;
}

.om-banner__text {
  color: #991b1b;
  font-size: 13px;
}

.om-card--table {
  overflow: hidden;
}

.om-table-scroll {
  overflow: auto;
  max-height: calc(100vh - 280px);
}

.om-table {
  width: 100%;
  border-collapse: separate;
  border-spacing: 0;
  min-width: 980px;
}

.om-table--wide {
  min-width: 1100px;
}

.om-col-tree { width: 36%; }
.om-col-pid { width: 8%; }
.om-col-src { width: 10%; }
.om-col-unit { width: 6%; }
.om-col-num { width: 8%; }

.om-th {
  position: sticky;
  top: 0;
  z-index: 4;
  background: #1e293b;
  color: #f8fafc;
  font-size: 11px;
  font-weight: 600;
  padding: 8px 10px;
  text-align: left;
  white-space: nowrap;
}

.om-th--num {
  text-align: right;
}

.om-th--sticky,
.om-td--sticky {
  position: sticky;
  left: 0;
  z-index: 3;
}

.om-th--sticky {
  z-index: 5;
}

.om-td {
  padding: 6px 10px;
  font-size: 12px;
  border-bottom: 1px solid #e2e8f0;
  background: #fff;
  vertical-align: middle;
}

.om-td--num {
  text-align: right;
  font-variant-numeric: tabular-nums;
}

.om-td--pid {
  color: #475569;
}

.om-tr--node .om-td {
  background: #f8fafc;
  font-weight: 600;
}

.om-tr--d0 .om-td { background: #eef2ff; }
.om-tr--d1 .om-td { background: #f1f5f9; }
.om-tr--d2 .om-td { background: #f8fafc; }
.om-tr--d3 .om-td { background: #fff; font-weight: 500; }
.om-tr--d4 .om-td { background: #fff; font-weight: 400; color: #334155; }

.om-tr:hover .om-td {
  background: #eff6ff !important;
}

.om-tree-cell {
  display: flex;
  align-items: center;
  gap: 4px;
}

.om-tree-label {
  min-width: 0;
}

.om-expand {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 22px;
  height: 22px;
  margin: 0 4px 0 0;
  padding: 0;
  border: 1px solid #cbd5e1;
  border-radius: 4px;
  background: #fff;
  cursor: pointer;
  flex-shrink: 0;
}

.om-expand--spacer {
  border: none;
  background: transparent;
  cursor: default;
}

.om-chevron {
  width: 0;
  height: 0;
  border-top: 5px solid transparent;
  border-bottom: 5px solid transparent;
  border-left: 6px solid #475569;
}

.om-chevron--open {
  transform: rotate(90deg);
}

.om-dash {
  color: #94a3b8;
}

.om-neg {
  color: #b91c1c;
}

.om-empty {
  background: #fff;
}

.om-empty__title {
  font-size: 16px;
  font-weight: 600;
  color: #0f172a;
}

.om-empty__text {
  margin-top: 4px;
  font-size: 13px;
  color: #64748b;
}

@media (max-width: 1280px) {
  .om-filters-toolbar {
    grid-template-columns: 1fr;
  }

  .om-filters-main {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .om-filters-actions {
    justify-content: flex-start;
  }
}

@media (max-width: 720px) {
  .om-filters-main {
    grid-template-columns: 1fr;
  }
}
</style>
